import { FC, useEffect, useRef, useCallback, useState } from 'react';
import track, { useTracking } from 'react-tracking';
import { Row, Column } from '@simplywallst/ui-core';
import Holding from '@/components/DeprecatedPortfolio/components/Holdings/components/Holding';
import { Dropdown } from '@components/Dropdown';
import Notification from '@components/Notification';
import { Button } from '@simplywallst/ui-core';
import Toggle from '@components/Toggle';

import { DeleteHoldingModalContainer } from '@/components/DeprecatedPortfolio/components/DeleteHoldingModal';
import type { DeleteHoldingModalPayload } from '@/components/DeprecatedPortfolio/components/DeleteHoldingModal/redux/interface';
import { TransactionsModalContainer } from '@/components/DeprecatedPortfolio/components/TransactionsModal';
import {
  ControlPanel,
  ShowAllContainer,
  HideLiquidatedCheckbox,
} from './styled';
import type { ComputedPortfolioHolding } from '@/components/DeprecatedPortfolio/redux/interface';

export enum HoldingsSortingOptions {
  CapitalGainWeek = 'capitalGainWeek',
  DividendReturn = 'dividendReturn',
  Name = 'name',
  CurrentValue = 'currentValue',
  SnowflakeScoreTotal = 'snowflakeScoreTotal',
  SnowflakeScoreValue = 'snowflakeScoreValue',
  SnowflakeScoreFuture = 'snowflakeScoreFuture',
  SnowflakeScoreDividend = 'snowflakeScoreDividend',
}

const getSortTerms = (
  isWatchlist: boolean,
  portfolioWithNoTransactions: boolean
) => [
  {
    option: HoldingsSortingOptions.CapitalGainWeek,
    ascending: true,
    label: '7 day Return',
  },
  {
    option: HoldingsSortingOptions.DividendReturn,
    ascending: true,
    label:
      isWatchlist || portfolioWithNoTransactions
        ? '1 year return'
        : 'Total Return',
  },
  { option: HoldingsSortingOptions.Name, ascending: true, label: 'Name A-Z' },
  {
    option: HoldingsSortingOptions.CurrentValue,
    ascending: true,
    label: 'Market Value',
  },
  {
    option: HoldingsSortingOptions.SnowflakeScoreTotal,
    ascending: true,
    label: 'Total Score',
  },
  {
    option: HoldingsSortingOptions.SnowflakeScoreValue,
    ascending: true,
    label: 'Value Score',
  },
  {
    option: HoldingsSortingOptions.SnowflakeScoreFuture,
    ascending: true,
    label: 'Future Score',
  },
  {
    option: HoldingsSortingOptions.SnowflakeScoreDividend,
    ascending: true,
    label: 'Dividend Score',
  },
];

interface ProfilePayload {
  settings?: {
    holdingsSorting?: string;
    showLiquidated?: boolean;
  };
}

interface Props {
  updateProfile: (payload: ProfilePayload) => void;
  handleDelete: (data: DeleteHoldingModalPayload) => void;
  handleEditTransactions: (data: { itemId: string }) => void;
  excludedCompanyCount: number;
  companiesAnalysedInPortfolio: number;
  holdingList: ComputedPortfolioHolding[];
  isWatchlist: boolean;
  portfolioWithNoTransactions: boolean;
  portfolioId: string | null;
  isPortfolioLinked: boolean;
  isEditable?: boolean;
  sortTerm?: string;
  showLiquidated?: boolean;
  loggedIn: boolean;
}

interface SortOption {
  option: string;
  ascending: boolean;
  label: string;
}

const HoldingList: FC<Props> = ({
  updateProfile,
  handleDelete,
  handleEditTransactions,
  excludedCompanyCount = 0,
  companiesAnalysedInPortfolio,
  holdingList = [],
  isWatchlist,
  portfolioWithNoTransactions,
  portfolioId,
  isPortfolioLinked,
  isEditable = false,
  sortTerm: defaultSortTerm,
  showLiquidated: defaultShowLiquidated,
  loggedIn,
}) => {
  const { trackEvent } = useTracking();
  const sortTerms = useRef<SortOption[]>(
    getSortTerms(isWatchlist, portfolioWithNoTransactions)
  );

  useEffect(() => {
    sortTerms.current = getSortTerms(isWatchlist, portfolioWithNoTransactions);
  }, [isWatchlist, portfolioWithNoTransactions]);

  const [sortTerm, setSortTerm] = useState(defaultSortTerm);

  useEffect(() => {
    if (
      typeof sortTerm === 'undefined' &&
      typeof defaultSortTerm !== 'undefined'
    ) {
      setSortTerm(defaultSortTerm);
    }
  }, [sortTerm, defaultSortTerm]);

  const handleSortHoldings = useCallback(
    (nextSortTerm) => {
      if (nextSortTerm !== sortTerm) {
        setSortTerm(nextSortTerm);
        if (loggedIn) {
          updateProfile({
            settings: {
              holdingsSorting: nextSortTerm,
            },
          });
        }
        trackEvent({
          action: 'click',
          subject: 'sort-holdings',
        });
      }
    },
    [trackEvent, sortTerm, updateProfile]
  );

  const [showLiquidated, setShowLiquidated] = useState(defaultShowLiquidated);

  useEffect(() => {
    if (
      typeof showLiquidated === 'undefined' &&
      typeof defaultShowLiquidated !== 'undefined'
    ) {
      setShowLiquidated(defaultShowLiquidated);
    }
  }, [showLiquidated, defaultShowLiquidated]);

  const handleShowLiquidated = useCallback(() => {
    setShowLiquidated((prev) => {
      updateProfile({
        settings: {
          showLiquidated: !prev,
        },
      });
      return !prev;
    });
  }, [updateProfile]);

  const [showAll, setShowAll] = useState(false);
  const handleShowAll = useCallback(() => {
    setShowAll(true);
    trackEvent({
      action: 'click',
      modifier: 'showAll',
    });
  }, [trackEvent]);

  const showCompanyLimitWarning = excludedCompanyCount > 0;

  return (
    <section>
      <ControlPanel provider={isPortfolioLinked}>
        {showCompanyLimitWarning && (
          <Notification warning small>
            {`Only ${companiesAnalysedInPortfolio} companies are analysed due to your plan limits.`}
          </Notification>
        )}
        <Row>
          <Column alignItem="flex-start" width="auto" marginRightAuto>
            <Dropdown
              styleType="compact"
              name="sortList"
              options={sortTerms.current}
              value={sortTerm || HoldingsSortingOptions.CapitalGainWeek}
              onSelect={handleSortHoldings}
            />
          </Column>
          {loggedIn && (
            <Column alignItem="flex-end" width="auto">
              <HideLiquidatedCheckbox>
                <Toggle
                  name="show-liquidated"
                  value={showLiquidated || false}
                  onChange={handleShowLiquidated}
                  label="Show Liquidated Holdings"
                />
              </HideLiquidatedCheckbox>
            </Column>
          )}
        </Row>
      </ControlPanel>
      <Row data-cy-id="holding-list-container">
        {holdingList
          .filter((n) => {
            return !(
              showLiquidated === false &&
              (n.currentValue === 0 || n.exclude)
            );
          })
          .sort((a, b) => {
            if (typeof sortTerm === 'undefined') return 0;
            if (typeof a[sortTerm] === 'string') {
              return a[sortTerm].localeCompare(b[sortTerm]);
            } else {
              return b[sortTerm] - a[sortTerm];
            }
          })
          .slice(0, showAll ? holdingList.length : 12)
          .map((holding, index) => {
            const {
              canonicalUrl,
              companyId,
              currentValueFormatted,
              dividendReturn,
              dividendReturnFormatted,
              exclude,
              isUpdating,
              name,
              snowflakeScoreAxes,
              tickerSymbol,
              uniqueSymbol,
              itemId,
              capitalGainWeek,
              capitalGainWeekFormatted,
              sharePrice,
              sharePriceFormatted,
            } = holding;
            return (
              <Column
                col={12}
                colSm={6}
                colMd={4}
                colLg={6}
                colXl={4}
                key={companyId}
              >
                <Holding
                  index={index}
                  capitalGainWeek={capitalGainWeek || 0}
                  capitalGainWeekFormatted={capitalGainWeekFormatted || '0.0%'}
                  portfolioId={portfolioId}
                  isEditable={isEditable}
                  itemId={itemId as string}
                  isWatchlist={isWatchlist}
                  portfolioWithNoTransactions={portfolioWithNoTransactions}
                  snowflakePoints={snowflakeScoreAxes}
                  currentValueFormatted={currentValueFormatted}
                  dividendReturn={dividendReturn}
                  dividendReturnFormatted={dividendReturnFormatted}
                  companyName={name}
                  companyId={companyId}
                  canonicalUrl={canonicalUrl}
                  isUpdating={isUpdating}
                  tickerSymbol={tickerSymbol}
                  uniqueSymbol={uniqueSymbol}
                  excluded={exclude}
                  showRemoveButton={isPortfolioLinked}
                  onDelete={handleDelete}
                  onEdit={handleEditTransactions}
                  sharePrice={sharePrice}
                  sharePriceFormatted={sharePriceFormatted}
                />
              </Column>
            );
          })}
      </Row>
      {holdingList.length > 1 && !showAll && (
        <Row>
          <ShowAllContainer>
            <Button
              styleType="gentleLarge"
              display="block"
              onClick={handleShowAll}
            >
              Show all
            </Button>
          </ShowAllContainer>
        </Row>
      )}

      {isEditable && <TransactionsModalContainer />}
      {isEditable && <DeleteHoldingModalContainer />}
    </section>
  );
};

const withTracking = track({
  type: 'track',
  subject: 'holdings',
})(HoldingList) as typeof HoldingList;

export { withTracking as HoldingList };
